'use client';

/**
 * Fetches and caches Saleor payment gateway configuration for the current checkout.
 *
 * Refetch inputs mirror breitling-storefront-next `useGetPaymentGateways`:
 * - `checkoutId` + `amount`
 * - `countryCode` = explicit billing country OR URL market country (not checkout addresses)
 * - `shopperLocale` from market configuration
 *
 * Debounces checkout id and amount so batched cart updates trigger one mutation.
 *
 * **Express drawer:** call once from `ExpressCheckout` only. Wallet session stores and
 * `useCartExpressCheckoutLoader` read `paymentGatewaysState` (configs + `isFetching`).
 */

import { useLocale } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import {
  PaymentGatewayInitializeDocument,
  SaleorPaymentGatewayInitializeMutation,
  SaleorPaymentGatewayInitializeMutationVariables,
} from '@/graphql/saleor/api';
import { useDebounce } from '@/hooks/useDebounce';
import { PAYMENT_GATEWAY_FETCH_DEBOUNCE_MS } from '@/lib/checkout/constants';
import {
  PAYMENT_TYPES,
  type PaymentType,
  type SplitPaymentConfig,
} from '@/lib/checkout/payments/types';
import { localeConfig } from '@/lib/configuration/configuration';
import { saleorMutate } from '@/lib/graphql/client';
import { devError } from '@/lib/utils/devLog';
import { checkoutState } from '@/states/checkout';
import {
  paymentGatewaysActions,
  paymentGatewaysState,
  type PaymentGatewayConfig,
} from '@/states/paymentGatewaysState';

const getPaymentAppIds = (): string[] =>
  process.env.NEXT_PUBLIC_PAYMENT_APPS_ID?.split(',').filter(Boolean) ?? [];

/** Normalizes router locale (`us-en`) or BCP-47 (`en-US`) for Adyen `shopperLocale`. */
function resolveShopperLocale(localeOption: string | undefined, routerLocale: string): string {
  if (!localeOption) {
    return localeConfig.parseLocale(routerLocale).locale.locale;
  }

  const isRouterLocale = localeOption.includes('-') && localeOption === localeOption.toLowerCase();
  if (isRouterLocale) {
    return localeConfig.parseLocale(localeOption).locale.locale;
  }

  return localeOption;
}

/**
 * Resolves `PaymentGatewayInitialize` inputs per Breitling rules:
 * billing override when provided, otherwise URL market country (not checkout addresses).
 */
function resolveGatewayFetchInputs({
  billingAddressCountryCode,
  localeOption,
  routerLocale,
}: {
  billingAddressCountryCode?: string;
  localeOption?: string;
  routerLocale: string;
}): { countryCode: string; shopperLocale: string } {
  const { country } = localeConfig.parseLocale(routerLocale);

  return {
    countryCode: billingAddressCountryCode ?? country.code,
    shopperLocale: resolveShopperLocale(localeOption, routerLocale),
  };
}

interface PaymentGatewayResponse {
  id: string;
  data: {
    paymentMethods?: Array<{
      name: string;
      type: string;
      [key: string]: unknown;
    }>;
    multicardMaxPayments?: number;
    nextPaymentMaxAmount?: number;
    nextPaymentMinAmount?: number;
    paidMoney?: { amount: number; currency: string };
  };
}

export interface UseGetPaymentGatewaysOptions {
  /** Override checkout/order id; defaults to `checkoutState.id`. */
  orderOrCheckoutId?: string;
  /** Override cart total; defaults to `checkoutState.checkout.totalPrice.gross.amount`. */
  amount?: number;
  /**
   * Billing country for gateway lookup on the full payment step.
   * When omitted, country is the URL market (`localeConfig.parseLocale`), not checkout addresses.
   */
  billingAddressCountryCode?: string;
  /** Override shopper locale for Adyen; defaults to market locale from configuration. */
  locale?: string;
}

/** Return shape of {@link useGetPaymentGateways}. */
export interface UseGetPaymentGatewaysResult {
  /** Debounced inputs differ from live cart — spinner window before refetch. */
  isSyncPending: boolean;
  /** `PaymentGatewayInitialize` request in flight; mirrored on `paymentGatewaysState.isFetching`. */
  isFetching: boolean;
  /** `isFetching || isSyncPending` — use on full checkout payment step, not express section loader. */
  isLoading: boolean;
  hasFetched: boolean;
  hasFoundPaymentMethods: boolean;
  gatewayConfigs: PaymentGatewayConfig[];
  splitPaymentConfig: SplitPaymentConfig | undefined;
  partialAmount: number | null;
  selectedMethod: PaymentType | null;
  error: string | null;
  /** Imperative refetch; clears gateway state before the mutation. */
  fetchPaymentGateways: () => Promise<void>;
}

/**
 * Loads Adyen gateway configs for the active checkout.
 *
 * Call once per feature tree that needs gateways (e.g. `ExpressCheckout` in the cart drawer).
 * Other consumers in the same tree should read `paymentGatewaysState` instead of subscribing again.
 *
 * @param options - Optional overrides; amount/id default to `checkoutState`
 * @returns Gateway configs and loading flags
 */
export function useGetPaymentGateways({
  orderOrCheckoutId,
  amount,
  billingAddressCountryCode,
  locale,
}: UseGetPaymentGatewaysOptions = {}): UseGetPaymentGatewaysResult {
  const snap = useSnapshot(checkoutState);
  const gatewaysSnap = useSnapshot(paymentGatewaysState);
  const routerLocale = useLocale();

  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [hasFoundPaymentMethods, setHasFoundPaymentMethods] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestGenerationRef = useRef(0);

  const checkoutId = orderOrCheckoutId ?? snap.id ?? undefined;
  const totalAmount = amount ?? snap.checkout?.totalPrice?.gross.amount;
  const { countryCode, shopperLocale } = useMemo(
    () =>
      resolveGatewayFetchInputs({
        billingAddressCountryCode,
        localeOption: locale,
        routerLocale,
      }),
    [billingAddressCountryCode, locale, routerLocale]
  );

  const debouncedCheckoutId = useDebounce(checkoutId, PAYMENT_GATEWAY_FETCH_DEBOUNCE_MS);
  const debouncedTotalAmount = useDebounce(totalAmount, PAYMENT_GATEWAY_FETCH_DEBOUNCE_MS);
  const debouncedCountryCode = useDebounce(countryCode, PAYMENT_GATEWAY_FETCH_DEBOUNCE_MS);
  const debouncedShopperLocale = useDebounce(shopperLocale, PAYMENT_GATEWAY_FETCH_DEBOUNCE_MS);

  const shouldSkipFetch = useCallback(() => {
    return !debouncedCheckoutId || debouncedTotalAmount === undefined || debouncedTotalAmount === 0;
  }, [debouncedCheckoutId, debouncedTotalAmount]);

  const isSyncPending =
    Boolean(checkoutId && totalAmount !== undefined && totalAmount > 0) &&
    (checkoutId !== debouncedCheckoutId ||
      totalAmount !== debouncedTotalAmount ||
      countryCode !== debouncedCountryCode ||
      shopperLocale !== debouncedShopperLocale);

  const parseGatewayConfigs = useCallback(
    (configs: PaymentGatewayResponse[]): PaymentGatewayConfig[] => {
      let splitPaymentConfig: SplitPaymentConfig | null = null;

      const gatewayConfigs: PaymentGatewayConfig[] = configs.map(({ data, id }) => {
        const paymentMethods =
          data?.paymentMethods?.map(({ name, type, ...dropInConfiguration }) => ({
            name: name as string,
            type: type as string,
            dropInConfiguration: dropInConfiguration as Record<
              string,
              string | string[] | number | object | boolean
            > | null,
          })) ?? [];

        if (data?.multicardMaxPayments) {
          splitPaymentConfig = {
            paidMoney: data?.paidMoney,
            maxPayments: data?.multicardMaxPayments,
            maxAmount: data?.nextPaymentMaxAmount ?? 0,
            minAmount: data?.nextPaymentMinAmount ?? 0,
          };
        }

        return { id, paymentMethods };
      });

      if (splitPaymentConfig) {
        paymentGatewaysActions.setSplitPaymentConfig(splitPaymentConfig);
      }

      return gatewayConfigs;
    },
    []
  );

  const fetchPaymentGateways = useCallback(async () => {
    if (shouldSkipFetch()) {
      setLoading(false);
      paymentGatewaysState.isFetching = false;
      return;
    }

    const checkoutIdForFetch = debouncedCheckoutId;
    const amountForFetch = debouncedTotalAmount;
    if (!checkoutIdForFetch || amountForFetch === undefined) {
      setLoading(false);
      paymentGatewaysState.isFetching = false;
      return;
    }

    const generation = ++requestGenerationRef.current;
    setLoading(true);
    paymentGatewaysState.isFetching = true;
    setError(null);

    const keepPartialAmount =
      checkoutIdForFetch !== paymentGatewaysState.id &&
      amountForFetch === paymentGatewaysState.amount;
    paymentGatewaysActions.clear(keepPartialAmount);

    try {
      const result = await saleorMutate<
        SaleorPaymentGatewayInitializeMutation,
        SaleorPaymentGatewayInitializeMutationVariables
      >(PaymentGatewayInitializeDocument, {
        id: checkoutIdForFetch,
        amount: amountForFetch,
        paymentGateways: getPaymentAppIds().map((id) => ({
          id,
          data: {
            countryCode: debouncedCountryCode,
            shopperLocale: debouncedShopperLocale,
          },
        })),
      });

      if (generation !== requestGenerationRef.current) {
        return;
      }

      if (result.paymentGatewayInitialize?.errors?.length) {
        const errors = result.paymentGatewayInitialize.errors;
        setError(errors[0].message || 'Failed to initialize payment gateways');
        devError('Error fetching payment gateways:', errors);
        return;
      }

      const gatewayConfigsRaw = result.paymentGatewayInitialize?.gatewayConfigs ?? [];
      type GatewayConfig = NonNullable<typeof gatewayConfigsRaw>[number];

      const configs = gatewayConfigsRaw
        .filter((config): config is GatewayConfig => {
          const configData = config?.data as Record<string, unknown> | null;
          return !!configData?.paymentMethods;
        })
        .map((config) => ({
          id: config.id,
          data: config.data as PaymentGatewayResponse['data'],
        })) as PaymentGatewayResponse[];

      const gatewayConfigs = parseGatewayConfigs(configs);

      const hasOfflineGateway = gatewayConfigs.some(
        (config) =>
          config.id === PAYMENT_TYPES.OFFLINE ||
          config.paymentMethods.some((method) => method.type === PAYMENT_TYPES.OFFLINE)
      );

      if (!hasOfflineGateway) {
        try {
          const offlineResponse = await fetch(
            `/api/payment/offline-availability?id=${encodeURIComponent(checkoutIdForFetch)}`,
            { cache: 'no-store' }
          );

          if (generation !== requestGenerationRef.current) {
            return;
          }

          if (offlineResponse.ok) {
            const offlineResult = (await offlineResponse.json()) as {
              success?: boolean;
              data?: { available?: boolean };
            };

            if (offlineResult.success && offlineResult.data?.available) {
              gatewayConfigs.push({
                id: PAYMENT_TYPES.OFFLINE,
                paymentMethods: [
                  {
                    name: PAYMENT_TYPES.OFFLINE,
                    type: PAYMENT_TYPES.OFFLINE,
                    dropInConfiguration: null,
                  },
                ],
              });
            }
          }
        } catch (offlineError) {
          devError('Error fetching offline payment availability:', offlineError);
        }
      }

      if (generation !== requestGenerationRef.current) {
        return;
      }

      paymentGatewaysActions.set(checkoutIdForFetch, amountForFetch, gatewayConfigs);

      if (gatewayConfigs.length > 0) {
        setHasFoundPaymentMethods(true);
      }
    } catch (err) {
      if (generation !== requestGenerationRef.current) {
        return;
      }

      devError('Error fetching payment gateways:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch payment methods');
    } finally {
      if (generation === requestGenerationRef.current) {
        setHasFetched(true);
        setLoading(false);
        paymentGatewaysState.isFetching = false;
      }
    }
  }, [
    debouncedCheckoutId,
    debouncedTotalAmount,
    debouncedCountryCode,
    debouncedShopperLocale,
    parseGatewayConfigs,
    shouldSkipFetch,
  ]);

  useEffect(() => {
    fetchPaymentGateways();

    return () => {
      requestGenerationRef.current += 1;
    };
  }, [fetchPaymentGateways]);

  return {
    isSyncPending,
    isFetching: loading || gatewaysSnap.isFetching,
    isLoading: loading || isSyncPending,
    hasFetched,
    hasFoundPaymentMethods,
    gatewayConfigs: gatewaysSnap.configs as PaymentGatewayConfig[],
    splitPaymentConfig: gatewaysSnap.splitPaymentConfig,
    partialAmount: gatewaysSnap.partialAmount,
    selectedMethod: gatewaysSnap.selectedMethod,
    error,
    fetchPaymentGateways,
  };
}
